// @ts-strict-ignore
import * as React from 'react';
import {
    SectionsProps,
    MonitoringSectionFilter,
    ReviewsSection,
    StabilitySection,
    AnalyticsSection,
} from './MonitoringContainer';
import {
    AppStoreAvailableBuild,
    IntegrationType,
    MonitoringStatistic,
    PhasedRelease,
    RolloutMonitoring,
    StabilityMetricType,
    SubscriptionFeatureType,
} from '@/types';
import { AdoptionXAxisSeriesPoint, FeatureSection, GroupedMonitoringStatistic } from '@/typescriptHelpers';
import { ClockCircleTwoTone } from '@ant-design/icons';
import Typography from 'antd/lib/typography';
import { antGrey } from '../styles/globalStyles';
import { useThemeMode } from '../styles/themeMode';
import { MonitoringGeneralReleaseInfo } from './MonitoringGeneralReleaseInfo';
import { MonitoringMetricCardsGrid } from './MonitoringMetricCardsGrid';
import Select from 'antd/lib/select';
import Tooltip from 'antd/lib/tooltip';
import { getIsOTAPlatform } from '@/helpers';
import { displayNameForIntegrationType } from '@/helpers/integrationHelpers';
import { dayjs } from '@/extendedDayjs';
import { Content } from 'antd/lib/layout/layout';
import ReleaseFeedbackPrompt from './ReleaseFeedbackPrompt';
import { EmptyStateWrapper } from '../releaseEmptyState/EmptyStateWrapper';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';

const selectedMonitoringSection = (props: SectionsProps) => {
    const shouldShowReviews = !props.isRolloutSourcedFromBetaStep && !getIsOTAPlatform(props.platform);

    switch (props.section) {
        case MonitoringSectionFilter.All:
            return (
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
                    {(isDisabled) => {
                        return isDisabled ?
                                <>
                                    {shouldShowReviews && <ReviewsSection {...props} shouldShowDisabledUI={true} />}
                                    <StabilitySection {...props} shouldShowDisabledUI={true} />
                                    <AnalyticsSection {...props} shouldShowDisabledUI={true} />
                                </>
                            :   <>
                                    <StabilitySection {...props} />
                                    <AnalyticsSection {...props} />
                                    {shouldShowReviews && <ReviewsSection {...props} />}
                                </>;
                    }}
                </PlanRestrictedFeatureWrapper>
            );
        case MonitoringSectionFilter.Stability:
            return <StabilitySection {...props} />;
        case MonitoringSectionFilter.Analytics:
            return <AnalyticsSection {...props} />;
        case MonitoringSectionFilter.Reviews:
            return shouldShowReviews ? <ReviewsSection {...props} /> : null;
    }
};

interface RolloutMonitoringSummaryContentProps extends SectionsProps {
    displayWaitingForRelease: boolean;
    releaseVersion: string;
    setSelectedBuild: (build: AppStoreAvailableBuild) => void;
    handleMonitoringSectionFilterChange: (value: string) => void;
    isDemoMode: boolean;
    phasedRelease: PhasedRelease;
    appStoreIntegrationId?: IntegrationType;
    rolloutMonitoring: RolloutMonitoring;
    groupedMonitoringStatisticsForCards: GroupedMonitoringStatistic[];
    isMultiApk: boolean;
    isRolloutSourcedFromBetaStep?: boolean;
    platformRolloutStatus?: React.ReactNode;
    headerSection?: React.ReactNode;
    hideRolloutStatus?: boolean;
    hideRolloutActions?: boolean;
    hideMonitoringAutomations?: boolean;
    hideReleaseFeedbackPrompt?: boolean;
}

const TIME_X_AXIS_VALUE = 'time';
// Placeholder value for the (non-selectable) Adoption option shown when no
// adoption data is available for the release.
const ADOPTION_UNAVAILABLE_VALUE = 'adoption-unavailable';
// Fallback option value for an adoption source with no integration type.
const ADOPTION_GENERIC_VALUE = 'adoption';
const X_AXIS_STORAGE_KEY = 'runway.rolloutXAxis';

// An adoption-rate-over-time series (one per stability integration) that the
// rollout charts can use as their x-axis instead of time. `value` is a stable,
// cross-app identifier (the integration type) so the persisted x-axis
// preference applies to any app/release exposing the same source.
interface AdoptionXAxisSource {
    value: string;
    integrationType?: IntegrationType;
    points: AdoptionXAxisSeriesPoint[];
}

export const RolloutMonitoringSummaryContent: React.FC<RolloutMonitoringSummaryContentProps> = (
    props: RolloutMonitoringSummaryContentProps,
) => {
    const { mode } = useThemeMode();
    const isDarkMode = mode === 'dark';

    // Adoption-rate metrics carry a timestamp→adoption% series (backend
    // `RenderAdoptionRateForRollout`). Each becomes an x-axis option so charts
    // can be plotted against adoption % instead of time.
    const adoptionXAxisSources: AdoptionXAxisSource[] = React.useMemo(() => {
        const stats = props.rolloutMonitoring?.monitoringStatistics ?? [];
        const withPoints = stats
            .filter((stat) => stat.type === StabilityMetricType.AdoptionRate)
            .map((stat) => {
                const fullStat = stat as MonitoringStatistic;
                const rawPoints =
                    fullStat.pastDataPoints?.length ? fullStat.pastDataPoints
                    : fullStat.recentDataPoints?.length ? fullStat.recentDataPoints
                    : [];
                const points: AdoptionXAxisSeriesPoint[] = rawPoints
                    .map((point) => ({ ts: dayjs.utc(point.timestamp).valueOf(), pct: Number(point.value) }))
                    .filter((point) => !isNaN(point.ts) && !isNaN(point.pct))
                    .sort((a, b) => a.ts - b.ts);
                return { integrationType: fullStat.integrationId, points };
            })
            .filter((source) => source.points.length > 0);

        // Key each option by integration type so the stored preference is stable
        // across apps/releases. Disambiguate the rare case of two sources sharing
        // a type (values must stay unique for the Select).
        const usedValues = new Set<string>();
        return withPoints.map((source, index) => {
            const baseValue = source.integrationType || ADOPTION_GENERIC_VALUE;
            const value = usedValues.has(baseValue) ? `${baseValue}-${index}` : baseValue;
            usedValues.add(value);
            return { value, integrationType: source.integrationType, points: source.points };
        });
    }, [props.rolloutMonitoring]);

    // The selection is persisted so the user's x-axis preference sticks across
    // releases and sessions.
    const [selectedXAxis, setSelectedXAxis] = React.useState<string>(() => {
        try {
            return window.localStorage.getItem(X_AXIS_STORAGE_KEY) ?? TIME_X_AXIS_VALUE;
        } catch {
            return TIME_X_AXIS_VALUE;
        }
    });

    const handleXAxisChange = React.useCallback((value: string) => {
        setSelectedXAxis(value);
        try {
            window.localStorage.setItem(X_AXIS_STORAGE_KEY, value);
        } catch {
            // localStorage unavailable
        }
    }, []);

    const hasAdoptionData = adoptionXAxisSources.length > 0;

    // A persisted adoption selection may not match this release's sources; fall
    // back to time for display/mapping without clearing the stored preference.
    const isValidSelection =
        selectedXAxis === TIME_X_AXIS_VALUE || adoptionXAxisSources.some((source) => source.value === selectedXAxis);
    const effectiveXAxis = isValidSelection ? selectedXAxis : TIME_X_AXIS_VALUE;
    const adoptionXAxisSeries =
        effectiveXAxis === TIME_X_AXIS_VALUE ? undefined : (
            adoptionXAxisSources.find((source) => source.value === effectiveXAxis)?.points
        );

    const hasRolloutData =
        props.groupedMonitoringStatisticsForCards?.length > 0 ||
        !!props.phasedRelease ||
        !!props.rolloutStartedAt ||
        !!props.releasedAt ||
        hasAdoptionData;

    const isReleaseNotReady = !props.isReleaseFromScraper && props.displayWaitingForRelease;
    if (isReleaseNotReady) {
        return (
            <>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        height: '100%',
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            maxWidth: 435,
                            backgroundColor: antGrey.grey2,
                            borderRadius: 8,
                            padding: 30,
                            display: 'flex',
                            gap: 25,
                        }}
                    >
                        <ClockCircleTwoTone
                            twoToneColor={isDarkMode ? [antGrey.grey5, 'transparent'] : antGrey.grey5}
                            style={{ fontSize: 28, paddingTop: 4 }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Typography.Text
                                style={{
                                    fontSize: 24,
                                    fontWeight: 600,
                                    color: antGrey.grey8,
                                }}
                            >
                                Waiting for release
                            </Typography.Text>
                            <Typography.Text style={{ color: antGrey.grey8 }}>
                                Check back here after your update is released!
                            </Typography.Text>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const metricTypeFilter = (
        <div>
            <Select
                defaultValue={MonitoringSectionFilter.All}
                style={{ width: 300 }}
                onChange={props.handleMonitoringSectionFilterChange}
            >
                <Select.Option value={MonitoringSectionFilter.All} key={MonitoringSectionFilter.All}>
                    All metric types
                </Select.Option>
                <Select.Option value={MonitoringSectionFilter.Stability} key={MonitoringSectionFilter.Stability}>
                    Stability
                </Select.Option>
                <Select.Option value={MonitoringSectionFilter.Analytics} key={MonitoringSectionFilter.Analytics}>
                    Observability & analytics
                </Select.Option>
                {!props.isRolloutSourcedFromBetaStep && !getIsOTAPlatform(props.platform) && (
                    <Select.Option value={MonitoringSectionFilter.Reviews} key={MonitoringSectionFilter.Reviews}>
                        Reviews
                    </Select.Option>
                )}
            </Select>
        </div>
    );

    return (
        <>
            {props.headerSection}
            <div>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <MonitoringGeneralReleaseInfo
                        releaseId={props.releaseId}
                        selectedBuild={props.selectedBuild}
                        appStoreSelectedBuilds={props.appStoreSelectedBuilds}
                        setSelectedBuild={props.setSelectedBuild}
                        releasedAt={props.releasedAt}
                    />
                    {metricTypeFilter}
                </div>
            </div>
            <Content style={{ paddingTop: 20, paddingBottom: 80, gap: 16 }}>
                {!props.isReleaseFromScraper &&
                    !props.isDemoMode &&
                    !props.isRolloutSourcedFromBetaStep &&
                    !props.hideReleaseFeedbackPrompt && <ReleaseFeedbackPrompt {...props} />}
                <EmptyStateWrapper
                    appId={props.appId}
                    featureSections={[FeatureSection.RolloutTopSummary]}
                    releaseId={props.releaseId}
                    shouldForceDisable={hasRolloutData}
                    shouldCenterContent
                >
                    {(isInEmptyState) => (
                        <div>
                            <MonitoringMetricCardsGrid
                                isAutomatedPhasedRollout={props.isAutomatedPhasedRollout}
                                stepRunComponentProps={null}
                                phasedRelease={props.phasedRelease}
                                platform={props.platform}
                                appStoreType={props.appStoreType}
                                stabilityIntegrations={props.stabilityIntegrations}
                                appStoreIntegrationId={props.appStoreIntegrationId}
                                {...props}
                                selectedSection={props.section}
                                rolloutMonitoring={props.rolloutMonitoring}
                                selectedBuild={props.selectedBuild}
                                isLoading={props.isLoading}
                                groupedMonitoringStatistics={props.groupedMonitoringStatisticsForCards}
                                isRolloutSourcedFromBetaStep={props.isRolloutSourcedFromBetaStep}
                                rolloutStartedAt={props.rolloutStartedAt}
                                isPhased={props.isPhased}
                                calculatedDataPerSection={props.calculatedDataPerSection}
                                isMultiApk={props.isMultiApk}
                                releaseVersion={props.releaseVersion}
                                disableLoadShimmer={isInEmptyState}
                                autoHaltUnstablePhasedReleasesAutomationId={
                                    props.autoHaltUnstablePhasedReleasesAutomationId
                                }
                                autoPromoteStablePhasedReleasesAutomationId={
                                    props.autoPromoteStablePhasedReleasesAutomationId
                                }
                                autoStagedRolloutAutomationId={props.autoStagedRolloutAutomationId}
                            />
                        </div>
                    )}
                </EmptyStateWrapper>

                <Divider
                    dashed
                    style={{
                        marginBottom: 0,
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 8,
                        marginTop: 20,
                    }}
                >
                    <Typography.Text style={{ color: antGrey.grey7 }}>X-axis:</Typography.Text>
                    <Select value={effectiveXAxis} style={{ width: 220 }} onChange={handleXAxisChange}>
                        <Select.Option value={TIME_X_AXIS_VALUE} key={TIME_X_AXIS_VALUE}>
                            Time
                        </Select.Option>
                        {hasAdoptionData ?
                            adoptionXAxisSources.map((source) => (
                                <Select.Option value={source.value} key={source.value}>
                                    {adoptionXAxisSources.length > 1 && source.integrationType ?
                                        `Adoption (${displayNameForIntegrationType(source.integrationType)})`
                                    :   'Adoption'}
                                </Select.Option>
                            ))
                        :   <Select.Option value={ADOPTION_UNAVAILABLE_VALUE} key={ADOPTION_UNAVAILABLE_VALUE} disabled>
                                <Tooltip title="Adoption data isn't available for this release yet.">
                                    <span>Adoption</span>
                                </Tooltip>
                            </Select.Option>
                        }
                    </Select>
                </div>
                <Space direction="vertical" style={{ width: '100%', paddingBottom: 40 }}>
                    {selectedMonitoringSection({
                        ...props,
                        adoptionXAxisSeries,
                    })}
                </Space>
            </Content>
        </>
    );
};
